/**
 * Mobile Drawer Controller
 * Handles the slide-out drawer for mobile view with touch gestures
 */

export class MobileDrawerController {
  constructor() {
    // DOM Elements
    this.drawer = document.getElementById('mobile-drawer');
    this.backdrop = document.getElementById('drawer-backdrop');
    this.toggleBtn = document.getElementById('drawer-toggle');
    this.closeBtn = document.getElementById('drawer-close');
    this.leaderboardList = document.getElementById('drawer-leaderboard');
    this.liveIndicator = document.getElementById('drawer-live-indicator');
    this.clockDisplay = document.getElementById('drawer-clock');
    this.progressBtn = document.getElementById('drawer-progress-btn');
    this.fullscreenBtn = document.getElementById('drawer-fullscreen-btn');
    this.swipeIndicator = document.getElementById('swipe-indicator');

    // State
    this.isOpen = false;
    this.isMobile = false;
    this.selectedTeam = null;

    // Drag state
    this.isDragging = false;
    this.startX = 0;
    this.startY = 0;
    this.currentX = 0;
    this.dragStartTime = 0;

    // Callbacks
    this.teamSelectHandler = null;
    this.progressHandler = null;
    this.fullscreenHandler = null;

    // Configuration
    this.EDGE_THRESHOLD = 30; // pixels from right edge to trigger swipe
    this.SWIPE_THRESHOLD = 50; // minimum swipe distance to trigger action
    this.MOBILE_BREAKPOINT = 768;

    this.init();
  }

  init() {
    this.checkViewport();
    this.setupEventListeners();
    this.setupTouchGestures();
  }

  checkViewport() {
    this.isMobile = window.innerWidth <= this.MOBILE_BREAKPOINT;

    // Auto-close drawer if resized to desktop
    if (!this.isMobile && this.isOpen) {
      this.close();
    }
  }

  setupEventListeners() {
    // Toggle button
    this.toggleBtn?.addEventListener('click', () => this.toggle());

    // Close button
    this.closeBtn?.addEventListener('click', () => this.close());

    // Backdrop click to close
    this.backdrop?.addEventListener('click', () => this.close());

    // Viewport resize
    window.addEventListener('resize', () => this.checkViewport());

    // Action buttons
    this.progressBtn?.addEventListener('click', () => {
      if (this.progressHandler) {
        this.progressHandler();
      }
      this.close();
    });

    this.fullscreenBtn?.addEventListener('click', () => {
      if (this.fullscreenHandler) {
        this.fullscreenHandler();
      }
      this.close();
    });

    // Keyboard accessibility
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });
  }

  setupTouchGestures() {
    // Edge swipe to open (on document)
    document.addEventListener('touchstart', (e) => this.handleDocumentTouchStart(e), { passive: true });
    document.addEventListener('touchmove', (e) => this.handleDocumentTouchMove(e), { passive: false });
    document.addEventListener('touchend', (e) => this.handleDocumentTouchEnd(e), { passive: true });

    // Swipe to close (on drawer)
    this.drawer?.addEventListener('touchstart', (e) => this.handleDrawerTouchStart(e), { passive: true });
    this.drawer?.addEventListener('touchmove', (e) => this.handleDrawerTouchMove(e), { passive: false });
    this.drawer?.addEventListener('touchend', (e) => this.handleDrawerTouchEnd(e), { passive: true });
  }

  // Document touch handlers (for edge swipe to open)
  handleDocumentTouchStart(e) {
    if (!this.isMobile || this.isOpen) return;

    const touch = e.touches[0];
    const screenWidth = window.innerWidth;

    // Check if touch started near right edge
    if (touch.clientX >= screenWidth - this.EDGE_THRESHOLD) {
      this.isDragging = true;
      this.startX = touch.clientX;
      this.startY = touch.clientY;
      this.currentX = touch.clientX;
      this.dragStartTime = Date.now();
    }
  }

  handleDocumentTouchMove(e) {
    if (!this.isDragging || this.isOpen) return;

    const touch = e.touches[0];
    const deltaX = this.startX - touch.clientX;
    const deltaY = Math.abs(touch.clientY - this.startY);

    // Ignore if scrolling vertically
    if (deltaY > Math.abs(deltaX)) {
      this.isDragging = false;
      return;
    }

    this.currentX = touch.clientX;

    // Show drawer preview while dragging
    if (deltaX > 10) {
      e.preventDefault();
      const drawerWidth = this.drawer.offsetWidth;
      const translateX = Math.max(0, drawerWidth - deltaX);
      this.drawer.style.transform = `translateX(${translateX}px)`;
      this.drawer.style.transition = 'none';

      // Show backdrop with opacity based on drag progress
      const progress = Math.min(deltaX / drawerWidth, 1);
      this.backdrop.style.opacity = progress * 0.5;
      this.backdrop.style.visibility = 'visible';
    }
  }

  handleDocumentTouchEnd(e) {
    if (!this.isDragging || this.isOpen) {
      this.isDragging = false;
      return;
    }

    const deltaX = this.startX - this.currentX;
    const velocity = deltaX / (Date.now() - this.dragStartTime);

    // Reset transition
    this.drawer.style.transition = '';
    this.backdrop.style.transition = '';

    // Open if swiped far enough or fast enough
    if (deltaX >= this.SWIPE_THRESHOLD || velocity > 0.5) {
      this.open();
    } else {
      // Reset drawer position
      this.drawer.style.transform = '';
      this.backdrop.style.opacity = '';
      this.backdrop.style.visibility = '';
    }

    this.isDragging = false;
  }

  // Drawer touch handlers (for swipe to close)
  handleDrawerTouchStart(e) {
    if (!this.isOpen) return;

    const touch = e.touches[0];
    this.isDragging = true;
    this.startX = touch.clientX;
    this.startY = touch.clientY;
    this.currentX = touch.clientX;
    this.dragStartTime = Date.now();
  }

  handleDrawerTouchMove(e) {
    if (!this.isDragging || !this.isOpen) return;

    const touch = e.touches[0];
    const deltaX = touch.clientX - this.startX;
    const deltaY = Math.abs(touch.clientY - this.startY);

    // Ignore if scrolling vertically
    if (deltaY > Math.abs(deltaX) && deltaX < 10) {
      return;
    }

    this.currentX = touch.clientX;

    // Only allow swiping right to close
    if (deltaX > 10) {
      e.preventDefault();
      this.drawer.style.transform = `translateX(${deltaX}px)`;
      this.drawer.style.transition = 'none';

      // Update backdrop opacity
      const drawerWidth = this.drawer.offsetWidth;
      const progress = 1 - Math.min(deltaX / drawerWidth, 1);
      this.backdrop.style.opacity = progress * 0.5;
    }
  }

  handleDrawerTouchEnd(e) {
    if (!this.isDragging || !this.isOpen) {
      this.isDragging = false;
      return;
    }

    const deltaX = this.currentX - this.startX;
    const velocity = deltaX / (Date.now() - this.dragStartTime);

    // Reset transition
    this.drawer.style.transition = '';
    this.backdrop.style.transition = '';

    // Close if swiped far enough or fast enough
    if (deltaX >= this.SWIPE_THRESHOLD || velocity > 0.5) {
      this.close();
    } else {
      // Reset drawer position
      this.drawer.style.transform = 'translateX(0)';
      this.backdrop.style.opacity = '';
    }

    this.isDragging = false;
  }

  open() {
    this.isOpen = true;
    this.drawer?.classList.add('open');
    this.backdrop?.classList.add('open');
    this.drawer.style.transform = '';

    // Hide swipe indicator when drawer is open
    if (this.swipeIndicator) {
      this.swipeIndicator.style.display = 'none';
    }

    // Trap focus in drawer
    this.closeBtn?.focus();
  }

  close() {
    this.isOpen = false;
    this.drawer?.classList.remove('open');
    this.backdrop?.classList.remove('open');
    this.drawer.style.transform = '';
    this.backdrop.style.opacity = '';
    this.backdrop.style.visibility = '';

    // Show swipe indicator when drawer is closed (on mobile)
    if (this.swipeIndicator && this.isMobile) {
      this.swipeIndicator.style.display = '';
    }

    // Return focus to toggle button
    this.toggleBtn?.focus();
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Update leaderboard in drawer
   */
  updateLeaderboard(teams) {
    if (!this.leaderboardList) return;

    this.leaderboardList.innerHTML = teams.map((team, index) => {
      const pos = team.interpolated || team.latest;
      const rank = index + 1;
      const isTop3 = rank <= 3;
      const isSelected = team.serial === this.selectedTeam;
      const speed = pos?.speed?.toFixed(1) || '--';
      const dtf = pos?.dtf?.toFixed(0) || '--';

      return `
        <div class="drawer-team-item${isSelected ? ' selected' : ''}" data-serial="${team.serial}">
          <div class="drawer-team-rank${isTop3 ? ' top-3' : ''}">${rank}</div>
          <div class="drawer-team-info">
            <div class="drawer-team-name">${team.name}</div>
            <div class="drawer-team-meta">
              <span class="drawer-team-speed">${speed} kn</span>
            </div>
          </div>
          <div class="drawer-team-dtf">${dtf} nm</div>
        </div>
      `;
    }).join('');

    // Add click handlers to team items
    this.leaderboardList.querySelectorAll('.drawer-team-item').forEach(item => {
      item.addEventListener('click', () => {
        const serial = item.dataset.serial;
        const team = teams.find(t => t.serial === serial);
        if (team && this.teamSelectHandler) {
          this.selectedTeam = serial;
          this.teamSelectHandler(team);
          this.updateLeaderboard(teams); // Re-render to show selection
          this.close();
        }
      });
    });
  }

  /**
   * Update live indicator
   */
  updateLiveIndicator(isLive) {
    if (!this.liveIndicator) return;

    if (isLive) {
      this.liveIndicator.classList.remove('paused');
      this.liveIndicator.querySelector('.live-text').textContent = 'LIVE';
    } else {
      this.liveIndicator.classList.add('paused');
      this.liveIndicator.querySelector('.live-text').textContent = 'PAUSED';
    }
  }

  /**
   * Update clock display
   */
  updateClock(timeString) {
    if (this.clockDisplay) {
      this.clockDisplay.textContent = timeString;
    }
  }

  /**
   * Set team selection handler
   */
  setTeamSelectHandler(handler) {
    this.teamSelectHandler = handler;
  }

  /**
   * Set progress button handler
   */
  setProgressHandler(handler) {
    this.progressHandler = handler;
  }

  /**
   * Set fullscreen button handler
   */
  setFullscreenHandler(handler) {
    this.fullscreenHandler = handler;
  }

  /**
   * Update progress button active state
   */
  setProgressActive(isActive) {
    if (this.progressBtn) {
      this.progressBtn.classList.toggle('active', isActive);
    }
  }

  /**
   * Set selected team from outside
   */
  setSelectedTeam(serial) {
    this.selectedTeam = serial;
  }
}
